import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import { Button } from "../components/ui/button";
import { CheckCircle2, ArrowLeft, Copy } from "lucide-react";
import { toast } from "sonner";
import { employerAPI } from "../services/api";

/**
 * Route: /employers/purchase/success?session_id=cs_...&ref=IV-EMP-######
 *
 * Return URL after a successful Stripe hosted Checkout. We poll the
 * backend status endpoint until the payment_status flips to "paid"
 * (webhook has landed) — this handles the small race window where
 * Stripe redirects the buyer here before the webhook has arrived.
 */
export default function EmployerPaySuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const ref = params.get("ref");
  const [status, setStatus] = useState("polling"); // polling | paid | error
  const [serviceReference, setServiceReference] = useState(ref || "");
  const pollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const MAX_ATTEMPTS = 30; // ~90s at 3s per poll
    const poll = async () => {
      attempts += 1;
      try {
        const s = await employerAPI.getPaymentStatus(sessionId);
        if (s.service_reference) setServiceReference(s.service_reference);
        if (s.payment_status === "paid") {
          setStatus("paid");
          if (pollRef.current) clearInterval(pollRef.current);
          return;
        }
        if (["expired", "failed", "refunded"].includes(s.payment_status)) {
          setStatus("error");
          if (pollRef.current) clearInterval(pollRef.current);
          return;
        }
      } catch (_e) {
        // Transient — keep polling.
      }
      if (attempts >= MAX_ATTEMPTS && pollRef.current) {
        clearInterval(pollRef.current);
        setStatus("error");
      }
    };
    // Fire immediately, then every 3s.
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [sessionId]);

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(serviceReference);
      toast.success("Service reference copied");
    } catch (_e) {
      toast.error("Unable to copy — please select the reference manually");
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="employer-purchase-success-page">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        {status === "polling" && (
          <div className="text-center" data-testid="purchase-status-polling">
            <div className="w-14 h-14 mx-auto rounded-full bg-blue-50 flex items-center justify-center mb-6">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              Confirming your payment…
            </h1>
            <p className="text-gray-600 leading-relaxed">
              We're finalising your Employer Intake Package purchase. This
              usually takes just a moment. Please don't close this tab.
            </p>
          </div>
        )}

        {status === "paid" && (
          <div className="text-center" data-testid="purchase-status-paid">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-6">
              <CheckCircle2 className="h-9 w-9 text-green-600" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Payment received. Thank you.
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto leading-relaxed">
              Your Employer Intake Package is confirmed. A receipt has been
              emailed to you by Stripe. The Inter-View team will follow up with
              next steps within one business day.
            </p>

            {serviceReference && (
              <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 mb-8">
                <span className="text-sm text-gray-600">Service reference:</span>
                <code
                  className="font-mono text-blue-800 font-semibold"
                  data-testid="purchase-service-reference"
                >
                  {serviceReference}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyRef}
                  className="h-8 px-2 text-blue-700 hover:text-blue-900"
                  data-testid="purchase-copy-reference"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            )}

            <div>
              <Link to="/employers">
                <Button
                  variant="outline"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                  data-testid="purchase-back-to-employers"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Inter-View for Employers
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="text-center" data-testid="purchase-status-error">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 flex items-center justify-center mb-6">
              <span className="text-3xl">⚠️</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              We can't confirm your payment right now.
            </h1>
            <p className="text-gray-600 leading-relaxed mb-6">
              This is unusual — no need to try paying again. Please email us at{" "}
              <a
                href="mailto:employers@inter-view.ca"
                className="text-blue-700 underline"
              >
                employers@inter-view.ca
              </a>{" "}
              with your service reference{" "}
              {serviceReference && (
                <code className="font-mono text-blue-800 font-semibold">
                  {serviceReference}
                </code>
              )}{" "}
              and we'll reconcile immediately.
            </p>
            <Link to="/employers">
              <Button variant="outline">Back to Inter-View for Employers</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
